import { StepperNavigation } from "@/components/StepperNavigation";
import { useStepper } from "@/components/StepperProvider";
import {
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { useFeature } from "@/hooks/use-feature";
import { cn } from "@/lib/utils";
import { api } from "@/services/auth-service";
import { Accordion } from "@radix-ui/react-accordion";
import { Progress } from "@radix-ui/react-progress";
import { ArrowRight, ChevronDown, ChevronUp, Plus, X } from "lucide-react";
import { useEffect, useState } from "react";
import { Alert, AlertDescription } from "../ui/alert";

const CHARACTER_TYPES = [
  "Custom",
  "Human",
  "Animal",
  "Angel",
  "Demon",
  "Dragon",
  "Elf",
  "Fairy",
  "Ghost",
  "Giant",
  "Goblin",
  "Mermaid",
  "Merman",
  "Robot",
  "Vampire",
  "Werewolf",
  "Witch",
  "Wizard",
];

const CharacterCard = ({ character, onEdit, onDelete, onStatusChange }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <Card className="relative mb-4 md:min-w-full bg-slate-50 dark:bg-input">
      <CardContent className="px-6 py-4">
        <div className="flex justify-end float-right gap-2 mb-2 mw-fit">
          <Button variant="ghost" size="sm" onClick={onEdit}>
            Edit
          </Button>
          <Button variant="ghost" size="sm" onClick={() => onDelete(character)}>
            <X className="w-4 h-4" />
          </Button>
        </div>

        <Accordion type="single" collapsible className="w-full">
          <AccordionItem value="details" className="border-none">
            <AccordionTrigger
              onClick={() => setIsExpanded(!isExpanded)}
              className="hover:no-underline pt-0 [&[data-state=open]>div>div>.expand-arrow]:rotate-180"
            >
              <div className="w-full">
                {/* Header section */}
                <div className="flex items-center w-full">
                  <div className="flex items-center">
                    {isExpanded ? (
                      <ChevronUp className="w-4 h-4 mr-2" />
                    ) : (
                      <ChevronDown className="w-4 h-4 mr-2" />
                    )}
                    <h3 className="text-lg font-semibold">{character.name}</h3>
                  </div>
                </div>
              </div>
            </AccordionTrigger>

            <AccordionContent>
              <div className="pt-4 space-y-4">
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li>• Age: {character.age} years old</li>
                  <li>• Type: {character.type}</li>
                  <li>
                    • Personality Traits:
                    <div className="flex flex-wrap gap-2 mt-2 ml-4">
                      {character.personalityTraits.map((trait, index) => (
                        <Badge key={index} variant="secondary">
                          {trait}
                        </Badge>
                      ))}
                    </div>
                  </li>
                  <li>
                    • Visual Traits:
                    <div className="flex flex-wrap gap-2 mt-2 ml-4">
                      {character.visualTraits.map((trait, index) => (
                        <Badge key={index} variant="outline">
                          {trait}
                        </Badge>
                      ))}
                    </div>
                  </li>
                </ul>
              </div>
            </AccordionContent>
          </AccordionItem>
        </Accordion>

        <div className="flex justify-end gap-4 mt-4">
          <div className="flex items-center space-x-2">
            <Checkbox
              id={`isMain-${character.name}`}
              checked={character.isMainCharacter}
              onCheckedChange={(checked) =>
                onStatusChange(character, "isMainCharacter", checked)
              }
            />
            <Label htmlFor={`isMain-${character.name}`}>Main Character</Label>
          </div>

          <div className="flex items-center space-x-2">
            <Checkbox
              id={`isAntagonist-${character.name}`}
              checked={character.isAntagonist}
              onCheckedChange={(checked) =>
                onStatusChange(character, "isAntagonist", checked)
              }
            />
            <Label htmlFor={`isAntagonist-${character.name}`}>Antagonist</Label>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const CharacterForm = ({ character, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    name: character?.name || "",
    age: character?.age || "",
    type: CHARACTER_TYPES.includes(character?.type)
      ? character?.type
      : character?.type
      ? "custom"
      : "Human",
    customType: CHARACTER_TYPES.includes(character?.type)
      ? ""
      : character?.type || "",
    personalityTraits: character?.personalityTraits || [],
    visualTraits: character?.visualTraits || [],
  });

  const [showCustomType, setShowCustomType] = useState(
    !CHARACTER_TYPES.slice(0, -1).includes(character?.type || "Human")
  );

  const handleTypeChange = (value) => {
    if (value === "Custom") {
      setShowCustomType(true);
      setFormData((prev) => ({ ...prev, type: "" }));
    } else {
      setShowCustomType(false);
      setFormData((prev) => ({ ...prev, type: value }));
    }
  };

  const handleTraitInput = (e, type) => {
    if (e.key === "Enter" && e.target.value) {
      setFormData((prev) => ({
        ...prev,
        [type]: [...prev[type], e.target.value.trim()],
      }));
      e.target.value = "";
    }
  };

  const removeTrait = (index, type) => {
    setFormData((prev) => ({
      ...prev,
      [type]: prev[type].filter((_, i) => i !== index),
    }));
  };

  const handleSave = () => {
    onSave(formData);
  };

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="name">Character Name*</Label>
        <Input
          id="name"
          placeholder={"Enter the characters name"}
          value={formData.name}
          onChange={(e) =>
            setFormData((prev) => ({ ...prev, name: e.target.value }))
          }
          required
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="age">Age*</Label>
        <Input
          id="age"
          placeholder={"Enter the characters age in years"}
          type="number"
          min="0"
          value={formData.age}
          onChange={(e) =>
            setFormData((prev) => ({
              ...prev,
              age: Math.max(0, parseInt(e.target.value) || 0),
            }))
          }
          required
        />
      </div>

      <div className="space-y-2">
        <Label>Character Type</Label>
        <Select
          value={showCustomType ? "Custom" : formData.type}
          onValueChange={handleTypeChange}
        >
          <SelectTrigger>
            <SelectValue placeholder="Select character type" />
          </SelectTrigger>
          <SelectContent>
            {CHARACTER_TYPES.map((type) => (
              <SelectItem key={type} value={type}>
                {type}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {showCustomType && (
          <Input
            placeholder="Enter character type"
            value={formData.customType}
            onChange={(e) =>
              setFormData((prev) => ({
                ...prev,
                customType: e.target.value,
                type: e.target.value,
              }))
            }
            className="mt-2"
          />
        )}
      </div>

      <div className="space-y-2">
        <Label>Visual Traits*</Label>
        <Input
          placeholder="Type then press Enter to add trait. (e.g., Blonde, Tall)"
          onKeyDown={(e) => handleTraitInput(e, "visualTraits")}
        />
        <div className="flex flex-wrap gap-2 mt-2">
          {formData.visualTraits.map((trait, index) => (
            <Badge
              key={index}
              className="cursor-pointer"
              onClick={() => removeTrait(index, "visualTraits")}
            >
              {trait} <X className="w-3 h-3 ml-1" />
            </Badge>
          ))}
        </div>
      </div>

      <div className="space-y-2">
        <Label>Personality Traits*</Label>
        <Input
          placeholder="Type then press Enter to add trait. (e.g., Shy, Clever)"
          onKeyDown={(e) => handleTraitInput(e, "personalityTraits")}
        />
        <div className="flex flex-wrap gap-2 mt-2">
          {formData.personalityTraits.map((trait, index) => (
            <Badge
              key={index}
              variant="secondary"
              className="cursor-pointer"
              onClick={() => removeTrait(index, "personalityTraits")}
            >
              {trait} <X className="w-3 h-3 ml-1" />
            </Badge>
          ))}
        </div>
      </div>

      <div className="flex justify-end pt-4 space-x-2">
        {onCancel && (
          <Button variant="outline" onClick={onCancel}>
            Cancel
          </Button>
        )}
        <Button onClick={handleSave} disabled={!formData.name}>
          Save Character
        </Button>
      </div>
    </div>
  );
};

const CharacterLimitAlert = ({ currentCount, planLimit, userPlan }) => {
  const progress = (Number(currentCount) / Number(planLimit || 1)) * 100;

  return (
    <div className="space-y-2 ">
      <div className="flex justify-between text-sm">
        <span className="text-muted-foreground">Characters Used</span>
        <span className="font-medium">
          {currentCount} / {planLimit}
        </span>
      </div>
      <Progress value={progress} />
      {currentCount >= planLimit && userPlan !== "FAMILY_PRO" && (
        <Alert variant="default" className="mt-2">
          <AlertDescription className="flex flex-col gap-2">
            <span>
              You've reached the character limit for your{" "}
              {userPlan === "FREE" ? "free" : "family"} plan.
            </span>
            <Button
              variant="link"
              className={cn(
                "h-auto p-0 font-normal dark:text-white w-fit",
                "active:scale-95 transition-transform",
                "group flex items-center gap-1"
              )}
              onClick={() => (window.location.href = "/plans")}
            >
              Upgrade your plan
              <ArrowRight
                className={cn(
                  "w-4 h-4 inline-block mt-0.5",
                  "group-hover:underline",
                  "group-hover:translate-x-1 transition-transform"
                )}
              />
            </Button>
          </AlertDescription>
        </Alert>
      )}
      {currentCount >= planLimit && userPlan === "FAMILY_PRO" && (
        <Alert variant="default" className="mt-2">
          <AlertDescription>
            You've reached the maximum number of characters allowed (5).
          </AlertDescription>
        </Alert>
      )}
    </div>
  );
};

const CharacterCreationStep = () => {
  const { formData, updateFormData } = useStepper();
  const [characters, setCharacters] = useState(formData.characters || []);
  const [savedCharacters, setSavedCharacters] = useState([]);
  const [editingCharacter, setEditingCharacter] = useState(null);
  const { limits } = useFeature();

  // Load saved characters on mount
  useEffect(() => {
    loadSavedCharacters();
  }, []);

  const userPlan = limits?.plan;
  const characterLimit = limits?.storyCharacters;
  const isAtLimit = characters.length >= characterLimit;

  const loadSavedCharacters = async () => {
    try {
      const response = await api.get("/api/characters/");
      setSavedCharacters(response.data);
    } catch (error) {
      console.error("Error loading saved characters:", error);
    }
  };

  const saveCharacter = async (characterData) => {
    try {
      const { ...apiData } = characterData;
      if (editingCharacter?.id) {
        // Edit existing character
        const response = await api.patch(
          `/api/characters/${editingCharacter.id}/`,
          apiData
        );
        setCharacters((prev) =>
          prev.map((char) =>
            char.id === editingCharacter.id ? { ...response.data } : char
          )
        );
      } else {
        // Create new character
        const response = await api.post("/api/characters/", apiData);
        setCharacters((prev) => [
          ...prev,
          {
            ...response.data,
            isMainCharacter: false,
            isAntagonist: false,
          },
        ]);
      }

      await loadSavedCharacters();
      setEditingCharacter(null);
    } catch (error) {
      console.error("Error saving character:", error);
    }
  };

  const updateCharacterStatus = (character, field, value) => {
    setCharacters((prev) =>
      prev.map((char) =>
        char.id === character.id ? { ...char, [field]: value } : char
      )
    );
  };

  const deleteCharacter = (characterToDelete) => {
    setCharacters((prev) =>
      prev.filter((char) => char.id !== characterToDelete.id)
    );
    if (editingCharacter?.id === characterToDelete.id) {
      setEditingCharacter(null);
    }
  };

  const addSavedCharacter = (savedCharacter) => {
    setCharacters((prev) => [
      ...prev,
      {
        ...savedCharacter,
        isMainCharacter: false,
        isAntagonist: false,
      },
    ]);
  };

  const handleNext = () => {
    updateFormData({
      characters: characters.map(
        ({ id, isMainCharacter, isAntagonist, ...rest }) => ({
          ...rest,
          id,
          isMainCharacter: isMainCharacter || false,
          isAntagonist: isAntagonist || false,
        })
      ),
    });
  };

  return (
    <div className="space-y-6">
      {/* Character List */}
      {characters.map((character) => (
        <CharacterCard
          key={character.id}
          character={character}
          onEdit={() => {
            if (editingCharacter?.id === character.id) {
              setEditingCharacter(null);
            } else {
              setEditingCharacter(null);
              setTimeout(() => {
                setEditingCharacter(character);
              }, 1);
            }
          }}
          onDelete={() => deleteCharacter(character)}
          onStatusChange={updateCharacterStatus}
        />
      ))}

      {!editingCharacter && (
        <CharacterLimitAlert
          currentCount={characters.length}
          planLimit={characterLimit}
          userPlan={userPlan}
        />
      )}

      {/* Character Form */}
      {editingCharacter ? (
        <Card>
          <CardContent className="pt-6">
            <CharacterForm
              character={editingCharacter}
              onSave={saveCharacter}
              onCancel={() => setEditingCharacter(null)}
            />
          </CardContent>
        </Card>
      ) : (
        <div className="flex flex-col gap-4 sm:flex-row">
          {/* Saved Characters Dropdown */}
          {savedCharacters.length > 0 && (
            <Select
              onValueChange={addSavedCharacter}
              value=""
              disabled={isAtLimit}
            >
              <SelectTrigger>
                <SelectValue placeholder="Load saved character" />
              </SelectTrigger>
              <SelectContent>
                {savedCharacters.map((character) => (
                  <SelectItem key={character.id} value={character}>
                    {`Name: ${character.name}, Age: ${character.age}, Type: ${character.type}`}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}

          {/* Add New Character Button */}
          <Button
            onClick={() => setEditingCharacter({})}
            className="w-full sm:w-auto"
            disabled={isAtLimit}
          >
            <Plus className="w-4 h-4 mr-2" /> Add New Character
          </Button>
        </div>
      )}

      <Separator />
      <StepperNavigation
        onNext={handleNext}
        nextDisabled={characters.length === 0}
      />
    </div>
  );
};

export default CharacterCreationStep;
